import { Fragment, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Modal, DatePicker, Select, Spin, Input } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import {
  FileDoneOutlined,
  DeleteOutlined,
  EditOutlined,
  BarsOutlined,
  CloudDownloadOutlined,
  LoadingOutlined,
  PlusOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { useDefaultCompany } from "../../../../../hooks/useCompanies";
import { useActivePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { useMenuItems, useMenuVats } from "../../../../../hooks/useMenu";
import { useCustomers, useEikLookup } from "../../../../../hooks/useCustomers";
import { useCreateInvoice } from "../../../../../hooks/useInvoices";
import {
  isValidBgVat,
  isValidEIK,
  normalizeVatInput,
} from "../../../../../lib/companyValidators";
import {
  INVOICE_NUMBER_LENGTH,
  nextInvoiceNumberOf,
} from "../../../../../lib/invoiceNumbering";
import { moneyToWordsBg } from "../../../../../lib/bgNumberToWords";
import {
  computeVatBreakdown,
  netUnitPrice,
  type VatLineBreakdown,
} from "../../../../../lib/vat";
import { R } from "../../orderingTheme";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
} from "../../modalPrimitives";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import type { Customer } from "../../../../../api/customers";
import type { Invoice } from "../../../../../api/invoices";
import InvoiceLineEditModal from "./InvoiceLineEditModal";
import InvoiceSummarizeModal from "./InvoiceSummarizeModal";
import { getApiErrorMessage } from "../../../../../utils/translations";

interface Props {
  open: boolean;
  onClose: () => void;
  bills: BillHistory[];
  // Извиква се със създадената фактура — родителят отваря preview.
  onSaved?: (invoice: Invoice) => void;
}

// Артикул в инвойса. Експортиран — ползва се и от InvoiceLineEditModal.
export type Line = {
  id: string;
  name: string;
  unit: string;
  vatRate: number;
  quantity: number;
  unitPrice: number;
  total: number; // винаги се рекалкулира от qty * unitPrice
};

/**
 * InvoiceCreateModal — стъпка 2 от Фактуриране workflow-а.
 *
 * За тази итерация: показва всички полета попълнени с правилни default-и
 * (доставчик от текущата фирма, активни методи на плащане, артикулите от
 * избраните сметки), без редакция на редовете, без save endpoint, без
 * клавиатурна навигация. Тези части ще се добавят в следващи итерации.
 */
export const InvoiceCreateModal = ({
  open,
  onClose,
  bills,
  onSaved,
}: Props) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();

  const { data: defaultCompany, isLoading: companyLoading } = useDefaultCompany(
    {
      enabled: open,
    },
  );
  const { data: activePMs = [], isLoading: pmLoading } =
    useActivePaymentMethods();
  const items = useMenuItems();
  const vats = useMenuVats();

  // ── Customer search dropdown ─────────────────────────────────────────────
  const [customerSearch, setCustomerSearch] = useState("");
  const { data: customerOptions = [] } = useCustomers(customerSearch, {
    enabled: open,
  });
  const eikLookup = useEikLookup();
  const createInvoiceMutation = useCreateInvoice();

  // ── Document header ─────────────────────────────────────────────────────
  const [invoiceType] = useState<"tax" | "simplified">("tax");
  const [issueDate, setIssueDate] = useState<Dayjs>(dayjs());
  const [supplyDate, setSupplyDate] = useState<Dayjs>(dayjs());
  const [supplyPlace, setSupplyPlace] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    setIssueDate(dayjs());
    setSupplyPlace("");
    // Дата на сделката = момента на закриване на сметката (bill.closed_at).
    // При няколко избрани сметки взимаме най-късното закриване — това е
    // моментът, в който фактически приключи цялата „сделка". Fallback на
    // днес само ако bills нямат closed_at (защитна стратегия — не би
    // трябвало да се случва за затворени сметки).
    const latestClosedAt = bills.reduce<number | null>((acc, b) => {
      if (!b.closed_at) return acc;
      const ts = new Date(b.closed_at).getTime();
      return acc === null || ts > acc ? ts : acc;
    }, null);
    setSupplyDate(latestClosedAt !== null ? dayjs(latestClosedAt) : dayjs());
  }, [open, bills]);

  // Когато идват default company / payment methods — попълваме default-ите.
  useEffect(() => {
    if (!open) return;
    if (defaultCompany) {
      // Същата формула, която рисува прегледа в „Номерация на документи“ —
      // едно място, за да не се разминат числото на екрана и на документа.
      setInvoiceNumber(nextInvoiceNumberOf(defaultCompany.last_invoice_number));
    }
  }, [open, defaultCompany]);

  useEffect(() => {
    if (!open) return;
    if (paymentMethodId !== null) return;
    if (activePMs.length === 0) return;
    // Ако всички сметки са пуснати с един метод — него предлагаме като default.
    const uniquePmIds = Array.from(
      new Set(
        bills
          .map((b) => b.payment_method_id)
          .filter((v): v is number => v !== null && v !== undefined),
      ),
    );
    if (uniquePmIds.length === 1) {
      setPaymentMethodId(uniquePmIds[0]);
    } else {
      setPaymentMethodId(activePMs[0].id);
    }
  }, [open, activePMs, bills, paymentMethodId]);

  // ── Клиент ───────────────────────────────────────────────────────────────
  // Полетата може да бъдат попълнени по 3 начина:
  // 1) ръчно (всичко се пише);
  // 2) ЕИК + бутон → Търговски регистър;
  // 3) Изберете фирма → попълва от вече запазен клиент.
  const [customerEik, setCustomerEik] = useState("");
  const [customerVat, setCustomerVat] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerCity, setCustomerCity] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerMol, setCustomerMol] = useState("");
  const [pickedCustomerId, setPickedCustomerId] = useState<number | null>(null);

  // ── Получаване ──────────────────────────────────────────────────────────
  // „Получил" е свободно поле с памет от 1 име на клиент: при избор на
  // фирма зареждаме последното използвано лице като default. Празно при
  // save = не пипаме DB-то (предишното име остава). Ново непразно име
  // презаписва предишното.
  const [receivedBy, setReceivedBy] = useState("");
  const [receivedAt, setReceivedAt] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    if (!open) return;
    setCustomerEik("");
    setCustomerVat("");
    setCustomerName("");
    setCustomerCity("");
    setCustomerAddress("");
    setCustomerMol("");
    setPickedCustomerId(null);
    setCustomerSearch("");
    setReceivedBy("");
    setReceivedAt(dayjs());
  }, [open]);

  const eikIsValid = isValidEIK(customerEik.trim());

  const fillFromCustomer = (c: Customer) => {
    setPickedCustomerId(c.id);
    setCustomerEik(c.eik);
    setCustomerVat(c.vat_number ?? "");
    setCustomerName(c.company_name);
    setCustomerCity(c.city);
    setCustomerAddress(c.address);
    setCustomerMol(c.mol ?? "");
    setReceivedBy(c.last_received_by ?? "");
  };

  const handleEikLookup = async () => {
    if (!eikIsValid) {
      message.warning(t("invoiceCreate.lookupInvalidEik"));
      return;
    }
    try {
      const r = await eikLookup.mutateAsync(customerEik.trim());
      setCustomerVat(r.vat_number ?? "");
      setCustomerName(r.company_name);
      if (r.city) setCustomerCity(r.city);
      if (r.address) setCustomerAddress(r.address);
      if (r.mol) setCustomerMol(r.mol);
      setPickedCustomerId(null);
      message.success(t("invoiceCreate.lookupSuccess"));
    } catch (error: unknown) {
      message.error(getApiErrorMessage(error, t("invoiceCreate.lookupFailed")));
    }
  };

  // ── Line items ──────────────────────────────────────────────────────────
  // Lines държим в local state — операторът може да ги редактира, изтрива,
  // да добавя нови и да „обобщава" сметката. Първоначално се пълнят от
  // bills, но след това не се re-derivat-ват от тях.
  const itemNameById = useMemo(() => {
    const m = new Map<number, { name: string; dds: number | null }>();
    items.forEach((it) => {
      const lang = it.itemLangs.find((l) => l.languageCode === "bg");
      m.set(it.id, {
        name: lang?.name ?? it.slug ?? `#${it.id}`,
        dds: it.dds ?? null,
      });
    });
    return m;
  }, [items]);

  const vatRateById = useMemo(() => {
    const m = new Map<number, number>();
    vats.forEach((v) => m.set(v.id, v.vatRate));
    return m;
  }, [vats]);

  const [lines, setLines] = useState<Line[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  // Summarize state — нужно е модалът да помни в кой режим сме и да може
  // да се връща обратно. При „single" пазим snapshot на предишните lines.
  const [summarizeMode, setSummarizeMode] = useState<"per-item" | "single">(
    "per-item",
  );
  const [linesBeforeSummary, setLinesBeforeSummary] = useState<Line[] | null>(
    null,
  );

  // Init / reset на lines при отваряне или смяна на bills.
  useEffect(() => {
    if (!open) return;
    const out: Line[] = [];
    bills.forEach((bill) => {
      bill.orders.forEach((order) => {
        // Skip split-out placeholder orders — BE-ът оставя на оригиналната
        // сметка елемент-дубликат (is_split=true, split_from_bill_id=null)
        // за продуктите, преместени в друга сметка. Ако не го пропуснем тук,
        // фактурата ще съдържа и преместените артикули и сумата ще е цялата
        // оригинална сметка вместо остатъка след разделяне.
        if (order.is_split && order.split_from_bill_id == null) return;
        order.items.forEach((it) => {
          if (it.is_cancelled) return;
          const meta = itemNameById.get(it.item_id);
          const vatRate =
            meta?.dds != null ? (vatRateById.get(meta.dds) ?? 0) : 0;
          const qty = Number(it.quantity ?? 0);
          const price = Number(it.price ?? 0);
          const sum = Number(it.total_sum ?? qty * price);
          out.push({
            id: `${bill.id}-${order.id}-${it.id}`,
            name: it.item_names?.bg || meta?.name || `Артикул #${it.item_id}`,
            unit: "бр.",
            vatRate,
            quantity: qty,
            unitPrice: price,
            total: sum,
          });
          // Добавките (additions) се водят отделно в bill payload-а, но в
          // sum-ата на сметката (bill.total_amount) са включени. Ако ги
          // пропуснем тук, invoice grand ще е по-малък от bill total и
          // mismatch confirm-ът ще се появи погрешно. Добавка няма собствен
          // флаг „анулирана" — сторно на продукта маха целия ред (по-горе).
          it.additions?.forEach((add) => {
            const addMeta = itemNameById.get(add.addition_item_id);
            const addVat =
              addMeta?.dds != null ? (vatRateById.get(addMeta.dds) ?? 0) : 0;
            const addQty = Number(add.quantity ?? 0);
            const addPrice = Number(add.price ?? 0);
            const addSum = Number(add.total_sum ?? addQty * addPrice);
            out.push({
              id: `${bill.id}-${order.id}-${it.id}-add-${add.id}`,
              name:
                add.item_names?.bg ||
                addMeta?.name ||
                `Добавка #${add.addition_item_id}`,
              unit: "бр.",
              vatRate: addVat,
              quantity: addQty,
              unitPrice: addPrice,
              total: addSum,
            });
          });
        });
      });
    });
    setLines(out);
    setSelectedLineId(out[0]?.id ?? null);
    setSummarizeMode("per-item");
    setLinesBeforeSummary(null);
  }, [open, bills, itemNameById, vatRateById]);

  // ── Line manipulation ───────────────────────────────────────────────────
  const newLocalId = () =>
    `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const recalcTotal = (l: Line): number => {
    const q = Number.isFinite(l.quantity) ? l.quantity : 0;
    const p = Number.isFinite(l.unitPrice) ? l.unitPrice : 0;
    return Number((q * p).toFixed(2));
  };

  const patchLine = (id: string, patch: Partial<Line>) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const merged = { ...l, ...patch };
        // total винаги се изчислява спрямо qty * unitPrice — никога не се
        // редактира директно.
        merged.total = recalcTotal(merged);
        return merged;
      }),
    );
  };

  const removeLine = (id: string) => {
    setLines((prev) => {
      const next = prev.filter((l) => l.id !== id);
      // Ако сме изтрили избрания, селекция отива на следващия наличен.
      if (selectedLineId === id) {
        setSelectedLineId(next[0]?.id ?? null);
      }
      return next;
    });
  };

  const addLine = (data: Omit<Line, "id" | "total">) => {
    const line: Line = {
      ...data,
      id: newLocalId(),
      total: recalcTotal({ ...data, id: "", total: 0 }),
    };
    setLines((prev) => [...prev, line]);
    setSelectedLineId(line.id);
  };

  const applySummarizeMode = (nextMode: "per-item" | "single") => {
    if (nextMode === summarizeMode) return;
    if (nextMode === "single") {
      if (lines.length === 0) return;
      // Snapshot преди collapse — за да може user-ът да върне „По име и цена".
      setLinesBeforeSummary(lines);
      // Групираме по ДДС ставка → един обобщен ред на група. Така ако
      // имаме напр. храна (9%) и алкохол (20%), получаваме 2 реда.
      const byRate = new Map<number, number>();
      lines.forEach((l) => {
        byRate.set(l.vatRate, (byRate.get(l.vatRate) ?? 0) + l.total);
      });
      // Подреждаме по ставка във възходящ ред — стабилен output.
      const collapsedLines: Line[] = Array.from(byRate.entries())
        .sort(([a], [b]) => a - b)
        .map(([rate, total]) => ({
          id: newLocalId(),
          name: t("invoiceCreate.summarizedName"),
          unit: "бр.",
          vatRate: rate,
          quantity: 1,
          unitPrice: Number(total.toFixed(2)),
          total: Number(total.toFixed(2)),
        }));
      setLines(collapsedLines);
      setSelectedLineId(collapsedLines[0]?.id ?? null);
      setSummarizeMode("single");
    } else {
      // Връщане към per-item: ако имаме snapshot — restore. Иначе оставяме
      // текущите редове (необичаен случай, но защитен).
      if (linesBeforeSummary) {
        setLines(linesBeforeSummary);
        setSelectedLineId(linesBeforeSummary[0]?.id ?? null);
      }
      setLinesBeforeSummary(null);
      setSummarizeMode("per-item");
    }
  };

  // ── Totals ──────────────────────────────────────────────────────────────
  // 5 стойности съответстващи на оригиналния POS формуляр:
  // - Облагаема сума: net на редовете с ДДС > 0
  // - Необлагаема сума: total на редовете с ДДС = 0
  // - ДДС: сума на ДДС от облагаемите редове
  // - Всичко сума: Облагаема + Необлагаема (преди ДДС)
  // - Обща стойност: Всичко сума + ДДС
  // Цялата ДДС разбивка (нето/ДДС/бруто на ред + груповите тотали) минава през
  // един споделен алгоритъм (lib/vat.ts), идентичен 1:1 с бекенда. Той гарантира,
  // че сумата на редовете ВИНАГИ е равна на Облагаема/ДДС/Обща стойност.
  const breakdown = useMemo(
    () =>
      computeVatBreakdown(
        lines.map((l) => ({ gross: l.total, rate: l.vatRate })),
      ),
    [lines],
  );

  // ── Забележка ───────────────────────────────────────────────────────────
  // По default: „Сметка: <id>" или „Сметки: <id1>, <id2>, …" — операторът
  // може да го промени. Възстановява се при отваряне/смяна на bills.
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open) return;
    if (bills.length === 0) {
      setNote("");
      return;
    }
    const ids = bills.map((b) => b.id).join(", ");
    setNote(
      bills.length === 1
        ? t("invoiceCreate.noteOneBill", { id: ids })
        : t("invoiceCreate.noteManyBills", { ids }),
    );
  }, [open, bills, t]);

  // ── Item action modals ──────────────────────────────────────────────────
  const [editLineId, setEditLineId] = useState<string | null>(null);
  const [deleteLineId, setDeleteLineId] = useState<string | null>(null);
  const [summarizeOpen, setSummarizeOpen] = useState(false);
  const [newLineOpen, setNewLineOpen] = useState(false);
  const [mismatchConfirmOpen, setMismatchConfirmOpen] = useState(false);

  // Сума на закритите сметки — против това сравняваме totals.grand при save,
  // за да предпазим оператора от случайно издаване на фактура с различна
  // сума от платените сметки.
  const billsTotal = useMemo(() => {
    return bills.reduce((acc, b) => acc + Number(b.total_amount ?? 0), 0);
  }, [bills]);

  const totalsMismatch = Math.abs(billsTotal - breakdown.grandTotal) > 0.01;

  // ── FE validation преди save ────────────────────────────────────────────
  // Per-field error map — позволява да маркираме точно полето в червено
  // (status="error" на antd) при клик на Запис. Showing-ът се пуска само
  // след първи опит за save, за да не „крещи" модалът при отваряне.
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => {
    if (!open) setShowErrors(false);
  }, [open]);

  const fieldErrors = useMemo((): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!invoiceNumber.trim() || !/^\d+$/.test(invoiceNumber)) {
      e.invoice_number = t("invoiceCreate.errInvoiceNumber");
    }
    if (!issueDate) e.issue_date = t("invoiceCreate.errDates");
    if (!supplyDate) e.supply_date = t("invoiceCreate.errDates");
    if (paymentMethodId == null) {
      e.payment_method = t("invoiceCreate.errPaymentMethod");
    }
    if (!customerEik.trim() || !isValidEIK(customerEik.trim())) {
      e.customer_eik = t("invoiceCreate.errCustomerEik");
    }
    if (customerVat.trim() && !isValidBgVat(customerVat.trim())) {
      e.customer_vat = t("invoiceCreate.errCustomerVat");
    }
    if (!customerName.trim()) {
      e.customer_name = t("invoiceCreate.errCustomerFields");
    }
    if (!customerCity.trim()) {
      e.customer_city = t("invoiceCreate.errCustomerFields");
    }
    if (!customerAddress.trim()) {
      e.customer_address = t("invoiceCreate.errCustomerFields");
    }
    if (lines.length === 0) {
      e.lines = t("invoiceCreate.errNoLines");
    }
    return e;
  }, [
    invoiceNumber,
    issueDate,
    supplyDate,
    paymentMethodId,
    customerEik,
    customerVat,
    customerName,
    customerCity,
    customerAddress,
    lines.length,
    t,
  ]);

  // Връща статус за дадено поле — undefined ако още не сме опитали save
  // или ако полето е валидно. „error" иначе → antd го рисува в червено.
  const errStatus = (key: string) =>
    showErrors && fieldErrors[key] ? "error" : undefined;

  const performSave = async () => {
    setMismatchConfirmOpen(false);
    try {
      const created = await createInvoiceMutation.mutateAsync({
        invoice_number: invoiceNumber
          .trim()
          .padStart(INVOICE_NUMBER_LENGTH, "0"),
        invoice_type: invoiceType,
        issue_date: issueDate.format("YYYY-MM-DD"),
        supply_date: supplyDate.format("YYYY-MM-DD"),
        supply_place: supplyPlace.trim() || null,
        payment_method_id: paymentMethodId,
        customer: {
          eik: customerEik.trim(),
          vat_number: customerVat.trim() || null,
          company_name: customerName.trim(),
          city: customerCity.trim(),
          address: customerAddress.trim(),
          mol: customerMol.trim() || null,
        },
        received_by: receivedBy.trim() || null,
        received_at: receivedAt ? receivedAt.format("YYYY-MM-DD") : null,
        note: note.trim() || null,
        bill_ids: bills.map((b) => b.id),
        lines: lines.map((l) => ({
          name: l.name,
          unit: l.unit,
          vat_rate: l.vatRate,
          quantity: l.quantity,
          unit_price: l.unitPrice,
          total: l.total,
        })),
      });
      message.success(t("invoiceCreate.saveSuccess"));
      onSaved?.(created);
    } catch (error: unknown) {
      // BE-то връща detail-а като `{bg: "...", en: "..."}` dict — пускаме го
      // през helper-а за да хванем правилния език (или legacy string).
      message.error(getApiErrorMessage(error, t("invoiceCreate.saveError")));
    }
  };

  const handleSaveClick = () => {
    setShowErrors(true);
    const firstError = Object.values(fieldErrors)[0];
    if (firstError) {
      message.warning(firstError);
      return;
    }
    if (totalsMismatch) {
      setMismatchConfirmOpen(true);
      return;
    }
    void performSave();
  };

  const selectedLine = lines.find((l) => l.id === selectedLineId) ?? null;
  const editLine = lines.find((l) => l.id === editLineId) ?? null;
  const deleteLine = lines.find((l) => l.id === deleteLineId) ?? null;

  // ── Modal sizing ────────────────────────────────────────────────────────
  const modalWidth = isMobile ? undefined : isTablet ? 880 : 1180;

  const loading = companyLoading || pmLoading;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="inv-create"
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "92vh",
        }}
      >
        <style>{`
          .inv-create .ant-input,
          .inv-create .ant-input-affix-wrapper,
          .inv-create .ant-input-number,
          .inv-create .ant-select .ant-select-selector,
          .inv-create .ant-picker {
            border-radius: 10px !important;
            border-color: ${R.border} !important;
            background: ${R.surface} !important;
            min-height: 38px !important;
            font-size: 13px;
            color: ${R.ink};
          }
          .inv-create .ant-input { height: 38px !important; padding: 4px 12px !important; }
          /* Когато Input има allowClear → той се wrap-ва в ant-input-affix-wrapper
             с вътрешен padding + .ant-input на 38px → реалната височина става ~46px.
             Фиксираме wrapper-а на 38px и нулираме inner-а, за да съвпада визуално
             с останалите полета (DatePicker, Select). */
          .inv-create .ant-input-affix-wrapper {
            height: 38px !important;
            padding: 0 11px !important;
          }
          .inv-create .ant-input-affix-wrapper > .ant-input {
            height: auto !important;
            padding: 0 !important;
            border: 0 !important;
            background: transparent !important;
            box-shadow: none !important;
          }
          .inv-create .ant-input:hover,
          .inv-create .ant-input:focus,
          .inv-create .ant-select:hover .ant-select-selector,
          .inv-create .ant-select-focused .ant-select-selector,
          .inv-create .ant-picker:hover,
          .inv-create .ant-picker-focused,
          .inv-create .ant-input-number:hover,
          .inv-create .ant-input-number-focused {
            border-color: ${R.brand} !important;
            box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
          }
          .inv-create .ant-input::placeholder { color: ${R.textMuted} !important; }

          /* Грешка → antd-та използва тези класове при status="error".
             Понеже горните общи правила имат !important, трябва да го override-нем,
             за да реално светне червено. */
          .inv-create .ant-input-status-error,
          .inv-create .ant-input-affix-wrapper-status-error,
          .inv-create .ant-input-number-status-error,
          .inv-create .ant-select-status-error .ant-select-selector,
          .inv-create .ant-picker-status-error {
            border-color: ${R.danger} !important;
            box-shadow: 0 0 0 3px rgba(220,38,38,0.12) !important;
            background: var(--color-danger-tint) !important;
          }
        `}</style>

        <ModalHeader
          icon={<FileDoneOutlined />}
          title={t("invoiceCreate.title")}
          subtitle={t("invoiceCreate.subtitle", { count: bills.length })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        {loading ? (
          <div
            style={{ padding: 60, display: "flex", justifyContent: "center" }}
          >
            <Spin />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: isNarrow ? "14px 16px" : "18px 24px",
              background: R.bg,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {/* Документ — на цяла ширина. На най-широки екрани (isDesktop)
                полетата се подреждат в 3 колони, така че по-малко скрол. */}
            <Section title={t("invoiceCreate.secDocument")}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow
                    ? "1fr"
                    : isDesktop
                      ? "1fr 1fr 1fr"
                      : "1fr 1fr",
                  gap: 10,
                }}
              >
                <Field
                  label={t("invoiceCreate.fieldNumber")}
                  required
                  hint={t("invoiceCreate.numberHint")}
                >
                  <Input
                    status={errStatus("invoice_number")}
                    value={invoiceNumber}
                    onChange={(e) =>
                      setInvoiceNumber(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldType")}>
                  <Select
                    value={invoiceType}
                    style={{ width: "100%" }}
                    options={[
                      { value: "tax", label: t("invoiceCreate.typeTax") },
                      {
                        value: "simplified",
                        label: t("invoiceCreate.typeSimplified"),
                      },
                    ]}
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldPaymentMethod")} required>
                  <Select
                    status={errStatus("payment_method")}
                    value={paymentMethodId ?? undefined}
                    style={{ width: "100%" }}
                    onChange={setPaymentMethodId}
                    options={activePMs.map((pm) => ({
                      value: pm.id,
                      label: pm.name_bg,
                    }))}
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldIssueDate")} required>
                  <DatePicker
                    status={errStatus("issue_date")}
                    value={issueDate}
                    onChange={(d) => d && setIssueDate(d)}
                    format="DD.MM.YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldSupplyDate")} required>
                  <DatePicker
                    status={errStatus("supply_date")}
                    value={supplyDate}
                    onChange={(d) => d && setSupplyDate(d)}
                    format="DD.MM.YYYY"
                    allowClear={false}
                    style={{ width: "100%" }}
                  />
                </Field>
                <Field
                  label={t("invoiceCreate.fieldSupplyPlace")}
                  hint={t("invoiceCreate.optional")}
                >
                  <Input
                    value={supplyPlace}
                    onChange={(e) => setSupplyPlace(e.target.value)}
                  />
                </Field>
              </div>
            </Section>

            {/* Клиент — на цяла ширина. На най-широки екрани (isDesktop)
                  „Pick a company" заема 2 от 3 колоните, а „Име на фирмата"
                  се качва вдясно на същия ред. */}
            <Section title={t("invoiceCreate.secCustomer")}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow
                    ? "1fr"
                    : isDesktop
                      ? "2fr 1fr"
                      : "1fr",
                  gap: 10,
                }}
              >
                <Field label={t("invoiceCreate.fieldPickCompany")}>
                  <Select
                    showSearch
                    placeholder={t("invoiceCreate.fieldPickCompanyPh")}
                    style={{ width: "100%" }}
                    value={pickedCustomerId ?? undefined}
                    onSearch={(v) => setCustomerSearch(v)}
                    onSelect={(id) => {
                      const c = customerOptions.find((x) => x.id === id);
                      if (c) fillFromCustomer(c);
                    }}
                    filterOption={false}
                    notFoundContent={
                      customerSearch
                        ? t("invoiceCreate.noCustomersForQuery")
                        : t("invoiceCreate.noCustomers")
                    }
                    allowClear
                    onClear={() => setPickedCustomerId(null)}
                    options={customerOptions.map((c) => ({
                      value: c.id,
                      label: `${c.company_name} · ЕИК ${c.eik}`,
                    }))}
                  />
                </Field>
                {isDesktop && (
                  <Field label={t("invoiceCreate.fieldName")} required>
                    <Input
                      status={errStatus("customer_name")}
                      value={customerName}
                      maxLength={255}
                      placeholder={t("invoiceCreate.phCustomerName")}
                      onChange={(e) => {
                        setCustomerName(e.target.value);
                        setPickedCustomerId(null);
                      }}
                    />
                  </Field>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr 1fr",
                  gap: 10,
                }}
              >
                <Field label={t("invoiceCreate.fieldCustomerEik")} required>
                  <Input
                    status={errStatus("customer_eik")}
                    value={customerEik}
                    maxLength={13}
                    inputMode="numeric"
                    placeholder={t("invoiceCreate.phCustomerEik")}
                    onChange={(e) => {
                      setCustomerEik(
                        e.target.value.replace(/\D/g, "").slice(0, 13),
                      );
                      setPickedCustomerId(null);
                    }}
                    suffix={
                      <button
                        type="button"
                        onClick={handleEikLookup}
                        disabled={!eikIsValid || eikLookup.isPending}
                        title={t("invoiceCreate.lookupTitle")}
                        aria-label={t("invoiceCreate.lookupTitle")}
                        style={{
                          height: 26,
                          width: 26,
                          borderRadius: 6,
                          border: "none",
                          background:
                            !eikIsValid || eikLookup.isPending
                              ? "transparent"
                              : R.brandTint,
                          color:
                            !eikIsValid || eikLookup.isPending
                              ? R.textMuted
                              : R.brandDeep,
                          cursor:
                            !eikIsValid || eikLookup.isPending
                              ? "not-allowed"
                              : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {eikLookup.isPending ? (
                          <LoadingOutlined style={{ fontSize: 13 }} />
                        ) : (
                          <CloudDownloadOutlined style={{ fontSize: 13 }} />
                        )}
                      </button>
                    }
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldVat")}>
                  <Input
                    status={errStatus("customer_vat")}
                    value={customerVat}
                    maxLength={20}
                    onChange={(e) => {
                      setCustomerVat(normalizeVatInput(e.target.value));
                      setPickedCustomerId(null);
                    }}
                    placeholder="BG123456789"
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldCity")} required>
                  <Input
                    status={errStatus("customer_city")}
                    value={customerCity}
                    maxLength={255}
                    placeholder={t("invoiceCreate.phCustomerCity")}
                    onChange={(e) => {
                      setCustomerCity(e.target.value);
                      setPickedCustomerId(null);
                    }}
                  />
                </Field>
              </div>

              {/* Име на фирмата — само на не-desktop, защото на desktop
                    вече е горе до „Изберете фирма". */}
              {!isDesktop && (
                <Field label={t("invoiceCreate.fieldName")} required>
                  <Input
                    status={errStatus("customer_name")}
                    value={customerName}
                    maxLength={255}
                    placeholder={t("invoiceCreate.phCustomerName")}
                    onChange={(e) => {
                      setCustomerName(e.target.value);
                      setPickedCustomerId(null);
                    }}
                  />
                </Field>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
                  gap: 10,
                }}
              >
                <Field label={t("invoiceCreate.fieldAddress")} required>
                  <Input
                    status={errStatus("customer_address")}
                    value={customerAddress}
                    maxLength={500}
                    placeholder={t("invoiceCreate.phCustomerAddress")}
                    onChange={(e) => {
                      setCustomerAddress(e.target.value);
                      setPickedCustomerId(null);
                    }}
                  />
                </Field>
                <Field
                  label={t("invoiceCreate.fieldMol")}
                  hint={t("invoiceCreate.optional")}
                >
                  <Input
                    value={customerMol}
                    maxLength={255}
                    placeholder={t("invoiceCreate.phCustomerMol")}
                    onChange={(e) => {
                      setCustomerMol(e.target.value);
                      setPickedCustomerId(null);
                    }}
                  />
                </Field>
              </div>
            </Section>

            {/* Получаване — на цяла ширина */}
            <Section title={t("invoiceCreate.secReceiving")}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
                  gap: 10,
                }}
              >
                <Field label={t("invoiceCreate.fieldReceivedBy")}>
                  <Input
                    value={receivedBy}
                    maxLength={255}
                    onChange={(e) => setReceivedBy(e.target.value)}
                    placeholder={t("invoiceCreate.fieldReceivedByPh")}
                    allowClear
                  />
                </Field>
                <Field label={t("invoiceCreate.fieldReceivedAt")}>
                  <DatePicker
                    value={receivedAt}
                    onChange={(d) => setReceivedAt(d)}
                    format="DD.MM.YYYY"
                    style={{ width: "100%" }}
                  />
                </Field>
              </div>
            </Section>

            {/* Артикули */}
            <Section
              title={t("invoiceCreate.secItems")}
              action={
                <div
                  style={{
                    display: "grid",
                    // На narrow → 2×2 grid, на широки → row (1×4) с auto-fit.
                    gridTemplateColumns: isNarrow
                      ? "1fr 1fr"
                      : "repeat(4, auto)",
                    gap: 6,
                  }}
                >
                  <SectionAction
                    icon={<BarsOutlined />}
                    label={t("invoiceCreate.actionSummarize")}
                    disabled={
                      lines.length === 0 && summarizeMode === "per-item"
                    }
                    onClick={() => setSummarizeOpen(true)}
                  />
                  <SectionAction
                    icon={<PlusOutlined />}
                    label={t("invoiceCreate.actionAdd")}
                    onClick={() => setNewLineOpen(true)}
                  />
                  <SectionAction
                    icon={<EditOutlined />}
                    label={t("invoiceCreate.actionEdit")}
                    disabled={!selectedLine}
                    onClick={() =>
                      selectedLine && setEditLineId(selectedLine.id)
                    }
                  />
                  <SectionAction
                    icon={<DeleteOutlined />}
                    label={t("invoiceCreate.actionDelete")}
                    disabled={!selectedLine}
                    onClick={() =>
                      selectedLine && setDeleteLineId(selectedLine.id)
                    }
                  />
                </div>
              }
            >
              <div
                style={{
                  background: R.surface,
                  border: `1px solid ${R.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                {/* Header-ът седи ВЪТРЕ в scroll container-а със
                    `position: sticky` — иначе при появяване на
                    вертикален scrollbar 1fr колоната на body-то се свива и
                    последните pixel-колони „избягат" вляво спрямо header-а.
                    Така header и rows винаги имат еднакъв „1fr" размер. */}
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: INVOICE_LINE_GRID,
                      minWidth: INVOICE_LINE_MIN_WIDTH,
                      gap: 8,
                      padding: "10px 12px",
                      background: R.bg,
                      borderBottom: `1px solid ${R.border}`,
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: R.textMuted,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <span>#</span>
                    <span>{t("invoiceCreate.colName")}</span>
                    <span>{t("invoiceCreate.colUnit")}</span>
                    <span>{t("invoiceCreate.colVat")}</span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colQty")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colUnitPrice")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colVatAmount")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colTotal")}
                    </span>
                    <span style={{ textAlign: "right" }}>
                      {t("invoiceCreate.colGross")}
                    </span>
                  </div>
                  {lines.length === 0 ? (
                    <div
                      style={{
                        padding: 24,
                        textAlign: "center",
                        color: R.textMuted,
                        fontSize: 13,
                      }}
                    >
                      {t("invoiceCreate.noLines")}
                    </div>
                  ) : (
                    lines.map((l, idx) => (
                      <LineRow
                        key={l.id}
                        line={l}
                        index={idx}
                        amounts={breakdown.lines[idx]}
                        isLast={idx === lines.length - 1}
                        isSelected={selectedLineId === l.id}
                        onSelect={() => setSelectedLineId(l.id)}
                      />
                    ))
                  )}
                </div>
              </div>
            </Section>

            {/* Обобщение */}
            <Section title={t("invoiceCreate.secTotals")}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
                  gap: 14,
                  alignItems: "start",
                }}
              >
                {/* Ляво: словом + забележка */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: R.textMuted,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {t("invoiceCreate.totalWords")}
                    </span>
                    <div
                      style={{
                        marginTop: 6,
                        padding: "10px 12px",
                        minHeight: 42,
                        background: R.bg,
                        border: `1px solid ${R.border}`,
                        borderRadius: 10,
                        fontSize: 13,
                        color: R.ink,
                        fontStyle: "italic",
                        lineHeight: 1.4,
                      }}
                    >
                      {moneyToWordsBg(breakdown.grandTotal)}
                    </div>
                  </div>
                  <div>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: R.textMuted,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {t("invoiceCreate.note")}
                    </span>
                    <div style={{ marginTop: 6 }}>
                      <Input.TextArea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={500}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Дясно: 5 стойности (2x2 + последна на цяла ширина) */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isNarrow ? "1fr 1fr" : "1fr 1fr",
                    gap: 8,
                  }}
                >
                  <TotalsRow
                    label={t("invoiceCreate.totalSum")}
                    value={breakdown.totalSum}
                  />
                  <TotalsRow
                    label={t("invoiceCreate.totalNonTaxable")}
                    value={breakdown.nonTaxable}
                  />
                  {breakdown.groups
                    .filter((g) => g.rate > 0)
                    .map((g) => (
                      <Fragment key={`vat-group-${g.rate}`}>
                        <TotalsRow
                          label={`${t("invoiceCreate.totalTaxable")} (${g.rate}%)`}
                          value={g.net}
                        />
                        <TotalsRow
                          label={`${t("invoiceCreate.totalVat")} (${g.rate}%)`}
                          value={g.vat}
                        />
                      </Fragment>
                    ))}
                  <div style={{ gridColumn: "1 / -1" }}>
                    <TotalsRow
                      label={t("invoiceCreate.totalGrand")}
                      value={breakdown.grandTotal}
                      emphasis
                    />
                  </div>
                </div>
              </div>
            </Section>
          </div>
        )}

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("invoiceCreate.save")}
            onClick={handleSaveClick}
            disabled={lines.length === 0}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>

      {/* Item action modals — sibling-ове на основния модал */}
      <InvoiceLineEditModal
        open={editLineId !== null}
        mode="edit"
        initial={editLine}
        onClose={() => setEditLineId(null)}
        onSubmit={(data) => {
          if (editLineId)
            patchLine(editLineId, {
              name: data.name,
              unit: data.unit,
              vatRate: data.vatRate,
              quantity: data.quantity,
              unitPrice: data.unitPrice,
            });
          setEditLineId(null);
        }}
      />

      <InvoiceLineEditModal
        open={newLineOpen}
        mode="create"
        initial={null}
        onClose={() => setNewLineOpen(false)}
        onSubmit={(data) => {
          addLine(data);
          setNewLineOpen(false);
        }}
      />

      <InvoiceSummarizeModal
        open={summarizeOpen}
        currentMode={summarizeMode}
        onClose={() => setSummarizeOpen(false)}
        onConfirm={(nextMode) => {
          applySummarizeMode(nextMode);
          setSummarizeOpen(false);
        }}
      />

      <Modal
        open={deleteLineId !== null}
        onCancel={() => setDeleteLineId(null)}
        onOk={() => {
          if (deleteLineId) removeLine(deleteLineId);
          setDeleteLineId(null);
        }}
        okText={t("invoiceCreate.deleteConfirmOk")}
        cancelText={t("common.cancel")}
        okButtonProps={{ danger: true }}
        centered
        title={
          <span
            style={{ display: "inline-flex", gap: 8, alignItems: "center" }}
          >
            <ExclamationCircleFilled style={{ color: R.danger }} />
            {t("invoiceCreate.deleteConfirmTitle")}
          </span>
        }
      >
        <p style={{ margin: 0, color: R.textSoft, fontSize: 13 }}>
          {t("invoiceCreate.deleteConfirmDesc", {
            name: deleteLine?.name ?? "",
          })}
        </p>
      </Modal>

      <Modal
        open={mismatchConfirmOpen}
        onCancel={() => setMismatchConfirmOpen(false)}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={isMobile ? undefined : isTablet ? 460 : 520}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div style={{ background: R.surface }}>
          <ModalHeader
            icon={<ExclamationCircleFilled />}
            title={t("invoiceCreate.mismatchTitle", {
              billsAmount: billsTotal.toFixed(2),
              context:
                bills.length > 1
                  ? t("invoiceCreate.mismatchManyBills")
                  : t("invoiceCreate.mismatchOneBill"),
            })}
            subtitle={t("invoiceCreate.mismatchBody")}
            onClose={() => setMismatchConfirmOpen(false)}
            isNarrow={isNarrow}
          />
          <div
            style={{
              padding: isNarrow ? "14px 16px" : "18px 24px",
              background: R.bg,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <MismatchStat
              label={t("invoiceCreate.mismatchBillsTotal")}
              value={billsTotal.toFixed(2)}
            />
            <MismatchStat
              label={t("invoiceCreate.mismatchInvoiceTotal")}
              value={breakdown.grandTotal.toFixed(2)}
              emphasis
            />
          </div>
          <ModalFooter isNarrow={isNarrow} narrowFirst>
            <GhostButton
              label={t("common.no")}
              onClick={() => setMismatchConfirmOpen(false)}
              shortcut={isDesktop ? "Esc" : undefined}
            />
            <PrimaryButton
              label={t("common.yes")}
              onClick={performSave}
              shortcut={isDesktop ? "⏎" : undefined}
            />
          </ModalFooter>
        </div>
      </Modal>
    </Modal>
  );
};

function MismatchStat({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        padding: "10px 12px",
        background: emphasis ? R.brandTint : R.surface,
        border: `1px solid ${emphasis ? R.brandTint2 : R.border}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          color: emphasis ? R.brandDeep : R.textMuted,
          letterSpacing: 0.8,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          fontFamily: R.mono,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 700,
          fontSize: 16,
          color: emphasis ? R.brandDeep : R.ink,
        }}
      >
        {value} €
      </div>
    </div>
  );
}

// ── Layout primitives ──────────────────────────────────────────────────────

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  // На narrow екран header-ът се качва над action-а вместо да е до него —
  // иначе action бутоните се притискат към малък ляв ръб (виж Артикули
  // секцията с 4 бутона) или влизат на 2 реда уж до title-а.
  const { isNarrow } = useViewport();
  return (
    <div
      style={{
        background: R.surface,
        border: `1px solid ${R.border}`,
        borderRadius: 12,
        padding: 14,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isNarrow && action ? "column" : "row",
          justifyContent: isNarrow && action ? "flex-start" : "space-between",
          alignItems: isNarrow && action ? "stretch" : "center",
          gap: isNarrow && action ? 8 : 0,
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            color: R.textMuted,
            letterSpacing: 1.2,
            textTransform: "uppercase",
          }}
        >
          {title}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: R.textSoft,
          letterSpacing: 0.2,
        }}
      >
        {label}
        {required && <span style={{ color: R.danger, marginLeft: 3 }}>*</span>}
        {hint && (
          <span
            style={{
              marginLeft: 6,
              fontWeight: 500,
              color: R.textMuted,
            }}
          >
            ({hint})
          </span>
        )}
      </span>
      {children}
    </div>
  );
}

function TotalsRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: number;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 12px",
        background: emphasis ? R.brandTint : R.bg,
        border: `1px solid ${emphasis ? R.brandTint2 : R.border}`,
        borderRadius: 10,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: emphasis ? R.brandDeep : R.textMuted,
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: emphasis ? 15 : 13,
          fontWeight: 700,
          color: emphasis ? R.brandDeep : R.ink,
          fontFamily: R.mono,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function SectionAction({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 30,
        padding: "0 10px",
        borderRadius: 8,
        border: `1px solid ${R.border}`,
        background: disabled ? R.bg : R.surface,
        color: disabled ? R.textMuted : R.brandDeep,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 12,
        fontWeight: 600,
        // display:flex (а не inline-flex) + justify center → бутонът се
        // разтегля да заеме grid cell-а ако той е 1fr, и остава
        // съдържание-широк ако cell-ът е auto.
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        whiteSpace: "nowrap",
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// ── Row + Cell editing ───────────────────────────────────────────────────

// Обща grid дефиниция за header + редове — 9 колони (като референтния POS):
// #, Наименование, Ед., ДДС %, Количество, Ед. цена (нето), Сума на ДДС,
// Сума (нето), Сума с ДДС (бруто). minWidth → хоризонтален scroll на тесни екрани.
const INVOICE_LINE_GRID =
  "32px minmax(120px, 1fr) 44px 56px 90px 82px 92px 82px 92px";
const INVOICE_LINE_MIN_WIDTH = 720;

function LineRow({
  line,
  index,
  amounts,
  isLast,
  isSelected,
  onSelect,
}: {
  line: Line;
  index: number;
  amounts: VatLineBreakdown;
  isLast: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  // Редовете са read-only — редакция става само през бутона „Промяна".
  // Click на ред = селекция (за бутоните „Промяна"/„Изтриване").
  //
  // Всички суми идват от споделената ДДС разбивка (lib/vat.ts), за да сумират
  // ТОЧНО на тоталите. Ед. цена е нетна, закръглена независимо (информативна);
  // Сума (нето) + Сума на ДДС = Сума с ДДС за всеки ред.
  const unitPriceNet = netUnitPrice(line.unitPrice, line.vatRate);
  const num: CSSProperties = {
    textAlign: "right",
    fontFamily: R.mono,
    fontVariantNumeric: "tabular-nums",
  };
  return (
    <div
      role="button"
      onClick={onSelect}
      style={{
        display: "grid",
        gridTemplateColumns: INVOICE_LINE_GRID,
        minWidth: INVOICE_LINE_MIN_WIDTH,
        gap: 8,
        height: 40,
        padding: "0 12px",
        borderBottom: isLast ? "none" : `1px solid ${R.border}`,
        fontSize: 12.5,
        color: R.ink,
        alignItems: "center",
        cursor: "pointer",
        background: isSelected ? R.brandTint : R.surface,
        transition: "background 120ms",
      }}
    >
      <span style={{ color: R.textMuted, fontFamily: R.mono }}>
        {index + 1}
      </span>
      <span
        style={{
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {line.name}
      </span>
      <span style={{ color: R.textSoft }}>{line.unit}</span>
      <span style={{ color: R.textSoft, fontFamily: R.mono }}>
        {line.vatRate}%
      </span>
      <span style={num}>{line.quantity}</span>
      <span style={{ ...num, color: R.textSoft }}>
        {unitPriceNet.toFixed(2)}
      </span>
      <span style={{ ...num, color: R.textSoft }}>
        {amounts.vat.toFixed(2)}
      </span>
      <span style={{ ...num, fontWeight: 600 }}>{amounts.net.toFixed(2)}</span>
      <span style={{ ...num, fontWeight: 600 }}>
        {amounts.gross.toFixed(2)}
      </span>
    </div>
  );
}

export default InvoiceCreateModal;
